/**
 * In-memory cache with TTL (Time To Live) support.
 * Prevents duplicate API calls and speeds up navigation.
 */

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
  fetchedAt: number;
}

class MemoryCache {
  private store = new Map<string, CacheEntry<unknown>>();

  /** Store a value with a TTL in milliseconds */
  set<T>(key: string, data: T, ttlMs: number): void {
    const now = Date.now();
    this.store.set(key, {
      data,
      fetchedAt: now,
      expiresAt: now + ttlMs,
    });
  }

  /** Retrieve a value if it exists and hasn't expired */
  get<T>(key: string): T | null {
    const entry = this.store.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return null;
    }
    return entry.data as T;
  }

  /** Check if a key exists and is still valid */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /** Invalidate a specific key */
  invalidate(key: string): void {
    this.store.delete(key);
  }

  /** Invalidate all keys matching a prefix */
  invalidatePrefix(prefix: string): void {
    for (const key of this.store.keys()) {
      if (key.startsWith(prefix)) {
        this.store.delete(key);
      }
    }
  }

  /** Clear the entire cache */
  clear(): void {
    this.store.clear();
  }

  /** Get cache stats for debugging */
  stats(): { size: number; keys: string[] } {
    return {
      size: this.store.size,
      keys: Array.from(this.store.keys()),
    };
  }
}

// Singleton instance shared across the app
export const cache = new MemoryCache();

// ─── TTL constants (ms) ───────────────────────────────────────────────────────
export const TTL = {
  SHORT:    30_000,   //  30 seconds — frequently changing data
  MEDIUM:   120_000,  //   2 minutes — product listings
  LONG:     300_000,  //   5 minutes — categories, static data
  VERY_LONG: 600_000, //  10 minutes — rarely changing data
} as const;

// ─── Cache key builders ───────────────────────────────────────────────────────
export const cacheKeys = {
  products: (filters: Record<string, unknown> = {}) =>
    `products:${JSON.stringify(filters)}`,
  product: (id: string) =>
    `product:${id}`,
  categories: () =>
    'categories:all',
  category: (id: string) =>
    `category:${id}`,
  featured: () =>
    'products:featured',
  related: (productId: string, categoryId: string) =>
    `products:related:${productId}:${categoryId}`,
} as const;
